import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ptBR, enUS, esES } from '@mui/material/locale';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dateFnsPtBR from 'date-fns/locale/pt-BR';
import dateFnsEnUS from 'date-fns/locale/en-US';
import dateFnsEs from 'date-fns/locale/es';
import i18n from '../i18n';

interface AppearanceSettings {
  theme: 'light' | 'dark' | 'auto';
  language: 'pt-BR' | 'en-US' | 'es-ES';
  compactMode: boolean;
  showTips: boolean;
}

interface ThemeContextType {
  settings: AppearanceSettings;
  currentTheme: Theme;
  isDarkMode: boolean;
  updateSettings: (newSettings: Partial<AppearanceSettings>) => void;
  resetToDefaults: () => void;
}

const defaultSettings: AppearanceSettings = {
  theme: 'light',
  language: 'pt-BR',
  compactMode: false,
  showTips: true,
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext deve ser usado dentro de um ThemeContextProvider');
  }
  return context;
};

const STORAGE_KEY = 'upclinic-appearance-settings';

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppearanceSettings>(defaultSettings);
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // Detectar preferência do sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPrefersDark(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Carregar configurações do localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem(STORAGE_KEY);
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...defaultSettings, ...parsed });
      } catch (error) {
        console.error('Erro ao carregar configurações de tema:', error);
      }
    }
  }, []);

  // Salvar configurações no localStorage
  const updateSettings = (newSettings: Partial<AppearanceSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSettings));
    
    // Aplicar configurações imediatamente
    applySettings(updatedSettings);
  };

  const resetToDefaults = () => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
    applySettings(defaultSettings);
  };

  // Aplicar configurações no DOM
  const applySettings = (settingsToApply: AppearanceSettings) => {
    // Aplicar classe de modo compacto no body
    if (settingsToApply.compactMode) {
      document.body.classList.add('compact-mode');
    } else {
      document.body.classList.remove('compact-mode');
    }

    // Aplicar idioma no HTML
    document.documentElement.lang = settingsToApply.language.split('-')[0];
  };

  // Determinar se deve usar tema escuro
  const isDarkMode = settings.theme === 'dark' || (settings.theme === 'auto' && systemPrefersDark);

  // Obter configuração de localização do Material-UI
  const getMuiLocale = () => {
    switch (settings.language) {
      case 'en-US':
        return enUS;
      case 'es-ES':
        return esES;
      case 'pt-BR':
      default:
        return ptBR;
    }
  };

  const getDateFnsLocale = () => {
    const lang = i18n.language || settings.language || 'pt-BR';
    if (lang.startsWith('en')) return dateFnsEnUS;
    if (lang.startsWith('es')) return dateFnsEs;
    return dateFnsPtBR;
  };

  // Criar tema customizado
  const currentTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      // Azul clínico profundo (padrão Care Connect Pro)
      primary: {
        main: isDarkMode ? '#3b82f6' : '#2563eb',
        light: '#60a5fa',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },
      secondary: {
        main: isDarkMode ? '#22d3ee' : '#0891b2',
        light: '#67e8f9',
        dark: '#0e7490',
        contrastText: '#ffffff',
      },
      success: {
        main: isDarkMode ? '#34d399' : '#059669',
        light: '#6ee7b7',
        dark: '#047857',
        contrastText: '#ffffff',
      },
      warning: {
        main: isDarkMode ? '#fbbf24' : '#d97706',
        light: '#fcd34d',
        dark: '#b45309',
        contrastText: '#ffffff',
      },
      info: {
        main: isDarkMode ? '#38bdf8' : '#0284c7',
        light: '#7dd3fc',
        dark: '#0369a1',
        contrastText: '#ffffff',
      },
      error: {
        main: isDarkMode ? '#f87171' : '#dc2626',
        light: '#fca5a5',
        dark: '#b91c1c',
        contrastText: '#ffffff',
      },
      background: {
        default: isDarkMode ? '#0b1220' : '#f8fafc',
        paper: isDarkMode ? '#151d2c' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? '#e5eaf2' : '#0f172a',
        secondary: isDarkMode ? '#94a3b8' : '#64748b',
      },
      divider: isDarkMode ? 'rgba(148, 163, 184, 0.16)' : 'rgba(15, 23, 42, 0.08)',
    },
    shape: {
      borderRadius: settings.compactMode ? 10 : 12,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: settings.compactMode ? '1.75rem' : '2rem',
        fontWeight: 700,
      },
      h2: {
        fontSize: settings.compactMode ? '1.5rem' : '1.75rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: settings.compactMode ? '1.25rem' : '1.5rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: settings.compactMode ? '1.125rem' : '1.25rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: settings.compactMode ? '1rem' : '1.125rem',
        fontWeight: 600,
      },
      h6: {
        fontSize: settings.compactMode ? '0.875rem' : '1rem',
        fontWeight: 600,
      },
      body1: {
        fontSize: settings.compactMode ? '0.875rem' : '1rem',
      },
      body2: {
        fontSize: settings.compactMode ? '0.75rem' : '0.875rem',
      },
    },
    spacing: settings.compactMode ? 6 : 8,
    components: {
      // Esc não fecha janelas: fechava formulários (ex.: Tabela de Preços) e apagava o que foi
      // digitado, sem perguntar (BUG-132). Fechar continua pelo X / Cancelar.
      MuiDialog: {
        defaultProps: {
          disableEscapeKeyDown: true,
        },
        // Celular: janela quase em tela cheia (antes ficava estreita com margens de 32px).
        styleOverrides: {
          paper: {
            '@media (max-width:599.95px)': {
              margin: 8,
              width: 'calc(100% - 16px)',
              maxWidth: 'calc(100% - 16px) !important',
              maxHeight: 'calc(100% - 16px)',
            },
          },
        },
      },
      // Abas roláveis: no celular as últimas abas ficavam cortadas fora da tela (R18).
      MuiTabs: {
        defaultProps: {
          variant: 'scrollable',
          scrollButtons: 'auto',
          allowScrollButtonsMobile: true,
        },
      },
      // Tabelas: no celular rolam para o lado em vez de cortar colunas (Financeiro, Home Care…).
      MuiTable: {
        styleOverrides: {
          root: {
            '@media (max-width:599.95px)': {
              display: 'block',
              overflowX: 'auto',
              maxWidth: '100%',
              WebkitOverflowScrolling: 'touch',
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: settings.compactMode ? 10 : 14,
            border: `1px solid ${isDarkMode ? 'rgba(148, 163, 184, 0.16)' : 'rgba(15, 23, 42, 0.08)'}`,
            // Sombra sutil (estilo Care Connect Pro), nada de sombra "flutuante"
            boxShadow: isDarkMode
              ? '0 1px 2px rgba(0, 0, 0, 0.35)'
              : '0 1px 2px rgba(16, 24, 40, 0.04)',
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: settings.compactMode ? 8 : 10,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: settings.compactMode ? '0.85rem' : '0.9rem',
            padding: settings.compactMode ? '6px 12px' : '8px 16px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: settings.compactMode ? 6 : 8,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(10px)',
            backgroundColor: isDarkMode 
              ? 'rgba(15, 23, 42, 0.8)' 
              : 'rgba(255, 255, 255, 0.8)',
          },
        },
      },
    },
  }, getMuiLocale());

  const value: ThemeContextType = {
    settings,
    currentTheme,
    isDarkMode,
    updateSettings,
    resetToDefaults,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={currentTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
          <CssBaseline />
          {children}
        </LocalizationProvider>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider; 